import { AxiosResponse, AxiosHeaders } from 'axios';
import { Admin, CreatedAdmin } from 'shared/types';
import ApiService from './api';
import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged, 
  onIdTokenChanged,
  getIdToken,
  getIdTokenResult,
  signInWithCustomToken,
  User 
} from "firebase/auth";
import { auth, functions } from 'services/firebase/firebase';
import { httpsCallable } from 'firebase/functions';
import apiService from './api';
import { store } from 'services/store';
import { clearImpersonationState, setImpersonationState } from 'services/store/reducers/authReducer';
import { clearSchools, fetchAllSchools } from 'services/store/reducers/schoolsReducer';
import { loginUserSuccess } from 'services/store/reducers/authReducer';
import { EAdminRoles } from 'shared/types';

type LoginResponse = Admin & {
  accessToken: string;
  refreshToken: string;
};

type ChangePasswordRequest = {
  password: string;
};

type ChangeTimezoneRequest = {
  timezone: number;
};

export const getCurrentUser = async (): Promise<AxiosResponse<LoginResponse>> =>
  await ApiService.get<LoginResponse>('users/me');

export const sendNewPassword = async (
  id: string,
  password: string,
  token: string | null
): Promise<AxiosResponse<void>> =>
  await ApiService.patch<ChangePasswordRequest, void>(
    `users/${id}/change-password`,
    {
      password
    },
    {
      headers: new AxiosHeaders({ Authorization: `Bearer ${token}` })
    }
  );

export const changeTimezone = async (id: string, timezone: number): Promise<AxiosResponse<void>> =>
  await ApiService.patch<ChangeTimezoneRequest, void>(`users/timezone/${id}`, {
    timezone
  }
);

class AuthService {
  private currentUser: User | null = null;
  private originalSuperAdminCustomToken: string | null = null;
  private impersonationCustomToken: string | null = null;
  private readonly impersonateCallable = httpsCallable<
    { targetUserId: string },
    { customToken?: unknown; returnCustomToken?: unknown }
  >(
    functions,
    'impersonateUser'
  );

  /** A non-empty string at `key` on a plain object, else null. Top level only, no aliases. */
  private stringField(source: unknown, key: string): string | null {
    if (!source || typeof source !== 'object') return null;
    const value = (source as Record<string, unknown>)[key];
    return typeof value === 'string' && value.trim().length > 0 ? value : null;
  }

  /**
   * Mints a custom token for the signed-in super-admin through the backend.
   * Only a fallback: impersonateUser returns the return token itself. Every
   * failure is kept so the error the admin sees names the status, not a guess.
   */
  private async fetchSelfReturnCustomToken(
    attempts: number
  ): Promise<{ token: string | null; lastError: string | null }> {
    let lastError: string | null = null;
    let delayMs = 150;
    for (let i = 0; i < attempts; i++) {
      try {
        const response = await apiService.post<undefined, { customToken?: unknown }>(
          '/admin/auth/custom-token/self',
          undefined
        );
        const token = this.stringField(response.data, 'customToken');
        if (token) return { token, lastError: null };
        lastError = `answered ${response.status} without a customToken`;
      } catch (error: any) {
        const status = error?.response?.status;
        const message = String(error?.response?.data?.message ?? error?.message ?? 'unknown error');
        lastError = status ? `answered ${status}: ${message}` : `failed: ${message}`;
      }
      this.logImpersonation('warn', 'return token mint failed', { attempt: i + 1, attempts, lastError });
      if (i < attempts - 1) {
        const waitMs = delayMs;
        await new Promise((r) => setTimeout(r, waitMs));
        delayMs = Math.min(delayMs * 2, 1200);
      }
    }
    return { token: null, lastError };
  }

  constructor() {
    onAuthStateChanged(auth, (user) => {
      this.currentUser = user;
    });
    onIdTokenChanged(auth, async (user) => {
      this.currentUser = user;
      await this.syncImpersonationFromClaims(user);
    });
  }

  getUser(): User | null {
    return this.currentUser;
  }

  private logImpersonation(
    level: 'info' | 'warn' | 'error',
    message: string,
    payload: Record<string, unknown> = {}
  ): void {
    const logger = level === 'error' ? console.error : level === 'warn' ? console.warn : console.info;
    logger(`[impersonation] ${message}`, payload);
  }

  private normalizeRole(role?: string | null): string {
    if (!role) return '';
    return role.toLowerCase().replace(/_/g, '-');
  }

  private normalizeEmail(email: string): string {
    return email.trim().toLowerCase();
  }

  async addAdmin(admin: CreatedAdmin): Promise<void> {
    try {
      await ApiService.post<Partial<CreatedAdmin>, void>('/users', {
        ...admin,
        email: this.normalizeEmail(admin.email),
      });
    } catch (error: any) {
      throw error;
    }
  }

  async login(email: string, password: string): Promise<{ firebaseUser: User; backendUser: any }> {
    const userCredential = await signInWithEmailAndPassword(auth, this.normalizeEmail(email), password);
    this.currentUser = userCredential.user;
  
    await userCredential.user.getIdToken(true);
    this.originalSuperAdminCustomToken = null;
    this.impersonationCustomToken = null;
    store.dispatch(clearImpersonationState());

    const response = await apiService.get<Admin>('/users/me');
    await userCredential.user.getIdToken(true);

    await apiService.post('/auth/activity', {
      type: 'REFRESH_CREATE',
    });
  
    await apiService.post('/auth/activity', {
      type: 'ACCESS_CREATE',
    });
  
    return {
      firebaseUser: userCredential.user,
      backendUser: response.data
    };
  }
  

  async logout(): Promise<void> {
    await signOut(auth);
    this.currentUser = null;
    this.originalSuperAdminCustomToken = null;
    this.impersonationCustomToken = null;
    store.dispatch(clearImpersonationState());
  }

  async getToken(): Promise<string | null> {
    if (!this.currentUser) return null;
    return await getIdToken(this.currentUser);
  }

  async impersonateUser(targetUserId: string): Promise<void> {
    const activeUser = auth.currentUser;
    if (!activeUser) {
      throw new Error('You must be authenticated');
    }
    
    const claims = (await getIdTokenResult(activeUser, true)).claims;
    const currentRole = this.normalizeRole((claims.role as string | undefined) ?? (claims.userType as string | undefined));
    const roleFromStore = this.normalizeRole(store.getState().auth.user?.role);
    const isSuperAdmin = currentRole === 'super-admin' || roleFromStore === 'super-admin';
    if (!isSuperAdmin) {
      throw new Error('Only super-admin can impersonate users');
    }
    if (claims.impersonatedBy) {
      throw new Error('Nested impersonation is not allowed');
    }
    if (activeUser.uid === targetUserId) {
      throw new Error('You cannot impersonate yourself');
    }

    await activeUser.getIdToken(true);
    this.logImpersonation('info', 'request started', {
      actorUid: activeUser.uid,
      targetUserId,
    });

    const response = await this.impersonateCallable({ targetUserId });
    const customToken = this.stringField(response.data, 'customToken');
    let returnToken = this.stringField(response.data, 'returnCustomToken');
    this.logImpersonation('info', 'response received', {
      hasCustomToken: Boolean(customToken),
      hasReturnToken: Boolean(returnToken),
    });

    if (!customToken) {
      throw new Error('The impersonation service did not return a sign-in token for this user.');
    }
    if (!returnToken) {
      // An impersonateUser build from before it minted the return token
      // itself. Still signed in as the super-admin here, so the backend can.
      const { token, lastError } = await this.fetchSelfReturnCustomToken(3);
      returnToken = token;
      if (!returnToken) {
        this.logImpersonation('error', 'no return token; impersonation cancelled', { lastError });
        throw new Error(
          'Impersonation cancelled: your super-admin session could not be saved for the way back ' +
            `(POST admin/auth/custom-token/self ${lastError ?? 'failed'}).`
        );
      }
    }
    this.originalSuperAdminCustomToken = returnToken;
    this.impersonationCustomToken = customToken;
    await signInWithCustomToken(auth, customToken);
    this.currentUser = auth.currentUser;
    const idToken = await auth.currentUser?.getIdToken(true);
    if (!idToken) {
      throw new Error('Impersonation token generation failed: ID token is empty');
    }
    const claimsAfterImpersonation = auth.currentUser
      ? (await getIdTokenResult(auth.currentUser, true)).claims
      : null;
    this.logImpersonation('info', 'token generated for impersonated session', {
      role: claimsAfterImpersonation?.role ?? claimsAfterImpersonation?.userType ?? null,
      schoolId: claimsAfterImpersonation?.schoolId ?? claimsAfterImpersonation?.school ?? null,
      hasImpersonatedBy: Boolean(claimsAfterImpersonation?.impersonatedBy),
    });

    await apiService.post('/auth/activity', {
      type: 'ACCESS_CREATE',
    });
    const responseUser = await getCurrentUser();
    store.dispatch(loginUserSuccess(responseUser.data));
    if (responseUser.data.role === EAdminRoles.SUPER_ADMIN) {
      await store.dispatch(fetchAllSchools());
    } else {
      store.dispatch(clearSchools());
    }
    await this.syncImpersonationFromClaims(auth.currentUser);
  }

  /**
   * Back to the super-admin. Restores the session from the return token
   * captured at impersonation time. That token lives only in memory and is
   * valid for an hour, so after a page reload, in a new tab, or after a long
   * session there is nothing to restore from: then the impersonated session
   * is signed out and the admin signs in again. Either way the admin is never
   * left stuck as the user.
   */
  async returnToSuperAdmin(): Promise<'restored' | 'signed-out'> {
    const activeUser = auth.currentUser;
    if (!activeUser) {
      throw new Error('You must be authenticated');
    }
    const returnToken = this.originalSuperAdminCustomToken;
    if (!returnToken) {
      this.logImpersonation('warn', 'no return token in memory; signing the impersonated session out', {
        currentUid: activeUser.uid,
      });
      await this.logout();
      return 'signed-out';
    }
    this.logImpersonation('info', 'return to super-admin started', { currentUid: activeUser.uid });
    try {
      await signInWithCustomToken(auth, returnToken);
    } catch (error: any) {
      // Custom tokens expire an hour after minting; Firebase refuses them after that.
      this.logImpersonation('warn', 'return token rejected; signing the impersonated session out', {
        code: error?.code ?? null,
      });
      await this.logout();
      return 'signed-out';
    }
    this.currentUser = auth.currentUser;
    const idToken = await auth.currentUser?.getIdToken(true);
    if (!idToken) {
      throw new Error('Failed to restore super-admin session: ID token is empty');
    }
    const restoredClaims = auth.currentUser ? (await getIdTokenResult(auth.currentUser, true)).claims : null;
    this.logImpersonation('info', 'super-admin session restored', {
      role: restoredClaims?.role ?? restoredClaims?.userType ?? null,
      schoolId: restoredClaims?.schoolId ?? restoredClaims?.school ?? null,
      impersonatedBy: restoredClaims?.impersonatedBy ?? null,
    });
    this.originalSuperAdminCustomToken = null;
    this.impersonationCustomToken = null;

    await apiService.post('/auth/activity', {
      type: 'ACCESS_CREATE',
    });
    const responseUser = await getCurrentUser();
    store.dispatch(loginUserSuccess(responseUser.data));
    if (responseUser.data.role === EAdminRoles.SUPER_ADMIN) {
      await store.dispatch(fetchAllSchools());
    } else {
      store.dispatch(clearSchools());
    }
    await this.syncImpersonationFromClaims(auth.currentUser);
    return 'restored';
  }

  async withSuperAdminForImpersonatedSession<T>(operation: () => Promise<T>): Promise<T> {
    if (!this.impersonationCustomToken || !this.originalSuperAdminCustomToken) {
      return operation();
    }

    try {
      await signInWithCustomToken(auth, this.originalSuperAdminCustomToken);
      this.currentUser = auth.currentUser;
      await auth.currentUser?.getIdToken(true);
      return await operation();
    } finally {
      await signInWithCustomToken(auth, this.impersonationCustomToken);
      this.currentUser = auth.currentUser;
      await auth.currentUser?.getIdToken(true);
    }
  }

  async syncImpersonationFromClaims(user: User | null): Promise<void> {
    if (!user) {
      store.dispatch(clearImpersonationState());
      return;
    }

    const tokenResult = await getIdTokenResult(user, true);
    const impersonatedBy = (tokenResult.claims.impersonatedBy as string | undefined) ?? null;
    const impersonatedAtClaim = tokenResult.claims.impersonatedAt as number | string | undefined;
    const impersonatedAt =
      typeof impersonatedAtClaim === 'number'
        ? impersonatedAtClaim
        : typeof impersonatedAtClaim === 'string'
          ? Number(impersonatedAtClaim)
          : null;

    if (!impersonatedBy) {
      store.dispatch(clearImpersonationState());
      return;
    }

    store.dispatch(
      setImpersonationState({
        impersonatedBy,
        impersonatedAt: Number.isFinite(impersonatedAt as number) ? (impersonatedAt as number) : null,
        originalSuperAdminUid: impersonatedBy,
      })
    );
  }
}

export const authService = new AuthService();